import { Badge } from '@/components/ui/badge';
import {
  Box,
  Shield,
  AlertTriangle,
  Building2,
  Network,
  Server,
  FileText,
  AlertCircle,
  CheckSquare,
  Briefcase,
  Cloud,
  HardDrive,
  Globe,
  type LucideIcon,
} from 'lucide-react';
import { EntityType } from '@/types/link';
import { useNavigate } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface RelationshipBadgeProps {
  entityType: EntityType;
  entityId: string;
  entityName: string;
  metadata?: Record<string, unknown>;
  onClick?: () => void;
}

const entityConfig: Record<EntityType, { icon: LucideIcon; color: string; route: string }> = {
  // Assets - neutral gray for domain-agnostic items
  asset: { icon: Box, color: 'bg-slate-500/10 text-slate-700 dark:text-slate-400', route: '/assets' },
  site: { icon: Building2, color: 'bg-slate-500/10 text-slate-700 dark:text-slate-400', route: '/assets' },
  zone: { icon: Network, color: 'bg-slate-500/10 text-slate-700 dark:text-slate-400', route: '/assets' },
  system: { icon: Server, color: 'bg-slate-500/10 text-slate-700 dark:text-slate-400', route: '/assets' },
  endpoint: { icon: HardDrive, color: 'bg-slate-500/10 text-slate-700 dark:text-slate-400', route: '/assets' },
  internet_connection: { icon: Globe, color: 'bg-slate-500/10 text-slate-700 dark:text-slate-400', route: '/assets?view=layout' },
  // Cloud - purple to match legend
  cloud: { icon: Cloud, color: 'bg-purple-500/10 text-purple-700 dark:text-purple-400', route: '/assets' },
  cloud_resource: { icon: Box, color: 'bg-purple-500/10 text-purple-700 dark:text-purple-400', route: '/assets' },
  // DMZ - red to match legend
  dmz_config: { icon: Shield, color: 'bg-red-500/10 text-red-700 dark:text-red-400', route: '/assets?view=layout' },
  // Governance - semantic colors
  task: { icon: CheckSquare, color: 'bg-slate-500/10 text-slate-700 dark:text-slate-400', route: '/tasks' },
  control: { icon: Shield, color: 'bg-green-500/10 text-green-700 dark:text-green-400', route: '/controls' },
  risk: { icon: AlertTriangle, color: 'bg-red-500/10 text-red-700 dark:text-red-400', route: '/risks' },
  incident: { icon: AlertCircle, color: 'bg-red-500/10 text-red-700 dark:text-red-400', route: '/incidents' },
  supplier: { icon: Briefcase, color: 'bg-slate-500/10 text-slate-700 dark:text-slate-400', route: '/suppliers' },
  evidence: { icon: FileText, color: 'bg-slate-500/10 text-slate-700 dark:text-slate-400', route: '/evidence' },
};

export function RelationshipBadge({ 
  entityType, 
  entityName, 
  metadata,
  onClick 
}: RelationshipBadgeProps) {
  const navigate = useNavigate();
  const config = entityConfig[entityType];
  const Icon = config.icon;

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(config.route);
    }
  };

  const tooltipContent = (
    <div className="space-y-1">
      <p className="font-medium">{entityName}</p>
      <p className="text-xs capitalize">{entityType.replace('_', ' ')}</p>
      {metadata && Object.entries(metadata).map(([key, value]) => (
        <p key={key} className="text-xs">
          <span className="opacity-70">{key}:</span> {String(value)}
        </p>
      ))}
    </div>
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className={`${config.color} cursor-pointer hover:opacity-80 transition-opacity`}
            onClick={handleClick}
          >
            <Icon className="w-3 h-3 mr-1" />
            {entityName}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>{tooltipContent}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
